import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthorisationKey } from '../../api/AuthApi';
import appleStoreLogo from '../../assets/svgs/app-store.svg';
import arrowRight from '../../assets/svgs/arrow-right.svg';
import playStoreLogo from '../../assets/svgs/google-play.svg';
import nervaLogoWithName from '../../assets/svgs/logos/nerva-logo-with-name.svg';
import tick from '../../assets/svgs/tick.svg';
import { MOBILE_OS } from '../../constants';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { useAuthStore } from '../../store/AuthStore';
import { colors } from '../../theme/colors';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: '20px',
    paddingTop: `${theme.spacing(3)}px`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `${theme.spacing(8)}px`,
    },
  },
  card: {
    width: '335px',
    backgroundColor: colors.secondLightGreen,
    height: '100%',
    borderRadius: 30,
    padding: theme.spacing(3),
    '& .MuiCardHeader-title': { textAlign: 'center' },
  },
}));

type LocationState = {
  isSubscribed: boolean;
  agreementStatus: boolean;
};
export const HCP_REFERRAL_COUPON_DISCOUNT_PERCENT = '25%';
export const PatientThankYouScreen: React.FunctionComponent = () => {
  const classes = useStyles();
  const location = useLocation<LocationState>();
  const userId = useAuthStore((state) => state.firebaseUser?.uid);
  const [patientToken, setPatientToken] = useState<string>();
  const [creatingToken, setCreatingToken] = useState<boolean>();
  const webOnboardingUrl =
    process.env.REACT_APP_WEB_ON_BOARDING_DOMAIN +
    '?coupon=hcp_referral' +
    `&token=${patientToken}`;

  useEffect(
    function fetchAuthenticationToken() {
      async function getAuthenticationToken() {
        setCreatingToken(true);
        const token = await getAuthorisationKey();
        setPatientToken(token);
        setCreatingToken(false);
      }

      if (!location.state.isSubscribed) {
        getAuthenticationToken();
      }
    },
    [userId, location.state.isSubscribed]
  );

  return (
    <Container maxWidth="xs">
      <Grid container xs={12} direction="column" classes={{ root: classes.root }}>
        <Box>
          <Typography variant="h4">
            {location.state.agreementStatus && <Box fontWeight={500}>Great Work!</Box>}
            {!location.state.agreementStatus && <Box fontWeight={500}>No problem!</Box>}
          </Typography>
        </Box>

        <Box textAlign="left" width="100%" marginTop="40px">
          <Box width="320px">
            {location.state.agreementStatus && (
              <Typography>
                You’ve successfully linked your Healthcare provider. Being linked may help you to:
              </Typography>
            )}
            {!location.state.agreementStatus && (
              <Typography>
                You can link with your healthcare provider later on if you’d like to. Being linked
                may help you to:
              </Typography>
            )}
          </Box>
        </Box>

        <Box textAlign="left" width="100%" display="flex" alignItems="center" marginTop="21px">
          <img src={tick} alt="tick" width="18px" height="18px" />
          <Box marginLeft="15px">
            <Typography>Keep your provider in the loop</Typography>
          </Box>
        </Box>

        <Box textAlign="left" width="100%" display="flex" alignItems="center" marginTop="21px">
          <img src={tick} alt="tick" width="18px" height="18px" />
          <Box marginLeft="15px">
            <Typography>Stay on track</Typography>
          </Box>
        </Box>

        <Box textAlign="left" width="100%" display="flex" alignItems="center" marginTop="21px">
          <img src={tick} alt="tick" width="18px" height="18px" />
          <Box marginLeft="15px">
            <Typography>Achieve better results</Typography>
          </Box>
        </Box>

        <Box textAlign="left" width="100%" marginTop="40px">
          <Box width="300px">
            <Typography>
              Well done — today you’ve completed the first step in taking back control of your IBS.
              You’re already on your way to better health!
            </Typography>
          </Box>
        </Box>

        {location.state.isSubscribed ? (
          <Box marginTop="50px">
            <Card classes={{ root: classes.card }}>
              <CardContent>
                <Box textAlign="center">
                  <Typography>
                    If you haven’t already downloaded the Nerva app, do so here:
                  </Typography>
                </Box>

                <Box textAlign="center" marginTop="30px">
                  <Link
                    href="https://apps.apple.com/us/app/nerva-ibs-relief-hypnotherapy/id1467398796"
                    target="_blank"
                    rel="noopener"
                    onClick={() => {
                      Analytics.track({
                        event: AnalyticsEventName.a_download_app_clicked,
                        store: MOBILE_OS.APPLE,
                      });
                    }}
                  >
                    <img src={appleStoreLogo} alt="apple store" />
                  </Link>
                </Box>

                <Box textAlign="center" marginTop="20px">
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.mindsethealth.ibs&hl=en_AU&gl=US"
                    target="_blank"
                    rel="noopener"
                    onClick={() => {
                      Analytics.track({
                        event: AnalyticsEventName.a_download_app_clicked,
                        store: MOBILE_OS.GOOGLE,
                      });
                    }}
                  >
                    <img src={playStoreLogo} alt="play store" />
                  </Link>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box marginTop="50px">
            <Card classes={{ root: classes.card }}>
              <CardContent>
                <Box>
                  <img src={nervaLogoWithName} alt="nerva logo" width="130px" height="35px" />
                </Box>
                <Box marginTop="30px" width="220px">
                  <Typography>Continue with Nerva by taking the IBS assessment.</Typography>
                </Box>

                <Box
                  marginTop="30px"
                  width="165px"
                  textAlign="left"
                  display="flex"
                  position="relative"
                >
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={creatingToken}
                    fullWidth
                    onClick={async () => {
                      document.cookie = `token=${patientToken}; domain=${process.env}`;
                      Analytics.track({
                        event: AnalyticsEventName.a_start_assessment_clicked,
                      });
                      window.open(webOnboardingUrl, '_blank');
                    }}
                    endIcon={
                      <img
                        src={arrowRight}
                        width={20}
                        height={20}
                        alt="resend email verification arrow"
                      />
                    }
                  >
                    Start
                  </Button>
                  {creatingToken && <CircularProgress size={24} />}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Grid>
    </Container>
  );
};
